import { MainLayout } from '@components/layouts/main.layout';
import { CampaignConsumedStudiesTile } from '@components/pages/campaigns/components/campaign-consumed-studies-tile.component';
import { CampaignDecisivePointsTile } from '@components/pages/campaigns/components/campaign-decisive-points-tile.component';
import { CampaignDetailsTile } from '@components/pages/campaigns/components/campaign-details-tile.component';
import { CampaignModal } from '@components/pages/campaigns/components/campaign-modal.component';
import { CampaignModalStepKey } from '@components/pages/campaigns/components/campaign-modal.shared';
import { CampaignScenariosTile } from '@components/pages/campaigns/components/campaign-scenarios-tile.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Skeleton } from '@components/ui/skeleton';
import { useGetCampaign } from '@queries/campaigns.query';
import { useGetCurrentUser } from '@queries/users.query';
import { routes } from '@routes';
import { canAccessMeiaCampaigns } from '@utils/meia-access.util';
import type { FC } from 'react';
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

type EditState = { initialStep: CampaignModalStepKey } | null;

export const CampaignDetailPage: FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { data: user, isPending: isUserPending } = useGetCurrentUser();
  const { data: campaign, error, isError, isLoading } = useGetCampaign({ campaignId });
  const navigate = useNavigate();
  const [editState, setEditState] = useState<EditState>(null);

  if (!isUserPending && !canAccessMeiaCampaigns(user)) {
    return <Navigate to={routes.home.path} />;
  }

  if (isLoading || isUserPending) {
    return (
      <MainLayout
        asideClassName="hidden"
        contentClassName="w-full md:max-w-full"
      >
        <div className="flex w-full flex-col gap-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-80 w-full" />
        </div>
      </MainLayout>
    );
  }

  if (!campaignId || isError || !campaign) {
    return (
      <MainLayout
        asideClassName="hidden"
        contentClassName="w-full md:max-w-full"
      >
        <div className="w-full">
          <div className="rounded-md border border-red-200 bg-red-50 p-5 text-sm text-red-700">
            {error?.errors[0]?.message ?? 'Unable to load campaign.'}
          </div>
        </div>
      </MainLayout>
    );
  }

  const handleEdit = (initialStep: CampaignModalStepKey): void => {
    setEditState({ initialStep });
  };

  return (
    <PageviewTracker>
      <HelmetWrapper title={`${campaign.name} | Campaigns`} />
      <MainLayout
        asideClassName="hidden"
        contentClassName="w-full md:max-w-full"
      >
        <div className="flex w-full flex-col gap-5">
          <div className="flex w-full flex-wrap items-start gap-5">
            <div className="flex min-w-0 flex-1 basis-full flex-col gap-5 md:basis-[calc(50%-0.625rem)]">
              <CampaignDetailsTile
                campaign={campaign}
                onEdit={() => handleEdit(CampaignModalStepKey.DETAILS)}
              />
              <CampaignDecisivePointsTile
                campaign={campaign}
                onEdit={() => handleEdit(CampaignModalStepKey.DECISIVE_POINTS)}
              />
            </div>
            <div className="flex min-w-0 flex-1 basis-full flex-col gap-5 md:basis-[calc(50%-0.625rem)]">
              <CampaignScenariosTile
                campaign={campaign}
                onEdit={() => handleEdit(CampaignModalStepKey.SCENARIOS)}
              />
              <CampaignConsumedStudiesTile
                campaign={campaign}
                onEdit={() => handleEdit(CampaignModalStepKey.CONSUMED_STUDIES)}
              />
            </div>
          </div>
        </div>
      </MainLayout>

      {editState && (
        <CampaignModal
          campaign={campaign}
          initialStep={editState.initialStep}
          isOpen={true}
          mode="edit"
          onClose={() => setEditState(null)}
          onDeleted={() => navigate(routes.campaigns.path)}
        />
      )}
    </PageviewTracker>
  );
};
